// SPDX-License-Identifier: MIT
// Custom SidePanel factory – overrides default Kepler.gl SidePanelFactory
// to insert the Annotations tab between Layer (Datasets) and Filter.

import React from 'react';
import { SidePanelFactory, Icons } from '@kepler.gl/components';
import { AnnotationsPanelTab } from '../integrations/feature-annotations/components/AnnotationsPanelTab';
import { AnnotationsIcon } from '../integrations/feature-annotations/components/AnnotationsIcon';

/** Panel id used for the Annotations side panel tab */
export const ANNOTATIONS_TAB_ID = 'annotations';

// Icon wrappers matching the format Kepler uses for its default panel icons
const LayerIcon: React.FC<any> = props => <Icons.Layers height='18px' {...props} />;
const FilterIcon: React.FC<any> = props => <Icons.FilterFunnel height='18px' {...props} />;
const InteractionIcon: React.FC<any> = props => <Icons.PointerClick height='18px' {...props} />;
const MapIcon: React.FC<any> = props => <Icons.BaseMap height='18px' {...props} />;

/**
 * Override SidePanelFactory to reorder tabs so Annotations sits between
 * Layer (datasets) and Filter, matching the stakeholder request.
 *
 * Order: Layer → Annotations → Filter → Interaction → Map
 */
export function CustomSidePanelFactory(
  Sidebar: any,
  PanelHeader: any,
  PanelToggle: any,
  LayerManager: any,
  FilterManager: any,
  InteractionManager: any,
  MapManager: any,
  CustomPanels: any
) {
  // Build the original SidePanel using the real factory
  const OriginalSidePanel = (SidePanelFactory as any)(
    Sidebar,
    PanelHeader,
    PanelToggle,
    LayerManager,
    FilterManager,
    InteractionManager,
    MapManager,
    CustomPanels
  );

  // Build the complete panel list with correct components and icons.
  // We reconstruct this using the manager components passed as factory deps
  // and Kepler's Icon components, then insert Annotations between Layer and Filter.
  const reorderedPanels = [
    {
      id: 'layer',
      label: 'sidebar.panels.layer',
      iconComponent: LayerIcon,
      component: LayerManager,
    },
    {
      id: ANNOTATIONS_TAB_ID,
      label: 'Annotations',
      iconComponent: AnnotationsIcon,
      component: AnnotationsPanelTab,
    },
    {
      id: 'filter',
      label: 'sidebar.panels.filter',
      iconComponent: FilterIcon,
      component: FilterManager,
    },
    {
      id: 'interaction',
      label: 'sidebar.panels.interaction',
      iconComponent: InteractionIcon,
      component: InteractionManager,
    },
    {
      id: 'map',
      label: 'sidebar.panels.basemap',
      iconComponent: MapIcon,
      component: MapManager,
    },
  ];

  return function CustomSidePanel(props: any) {
    return <OriginalSidePanel {...props} panels={reorderedPanels} />;
  };
}

// CRITICAL: Match the original factory's dependency list
CustomSidePanelFactory.deps = SidePanelFactory.deps;

/**
 * Replacement function for use with `injectComponents()`.
 */
export const replaceSidePanel = () => [SidePanelFactory, CustomSidePanelFactory];

export default CustomSidePanelFactory;
